<template>
  <v-container
    fluid
    class="pa-6">
    <!-- Filter Drawer -->
    <delivery-booking-list-filter
      v-model="filterDrawer"
      :filters="filters"
      @update:filters="filters = $event"
      @clear="clearFilters" />

    <!-- Header -->
    <div class="d-flex align-center mb-1">
      <div>
        <div class="text-h5 font-weight-bold">
          แดชบอร์ดภาพรวม
        </div>
        <div class="body-2 grey--text">
          สรุปข้อมูลการดำเนินงานประจำวันที่ {{ todayLabel }}
        </div>
      </div>
      <v-spacer />
      <v-badge
        :value="activeFilterCount > 0"
        :content="activeFilterCount"
        color="secondary"
        overlap>
        <v-btn
          depressed
          outlined
          color="secondary"
          @click="filterDrawer = true">
          <v-icon left>
            mdi-filter-variant
          </v-icon>
          ตัวกรองทั้งระบบ
        </v-btn>
      </v-badge>
    </div>

    <!-- Total bookings today card -->
    <v-row class="mt-4 mb-2">
      <v-col
        cols="12"
        sm="6"
        md="3">
        <v-card
          rounded="lg"
          elevation="1"
          class="pa-4 cursor-pointer"
          @click="goToToday">
          <div class="d-flex align-center">
            <v-icon color="grey lighten-1">
              mdi-calendar
            </v-icon>
            <div class="caption grey--text ml-4">
              การจองทั้งหมดวันนี้
            </div>
          </div>
          <div class="text-h4 text-right font-weight-bold mt-2">
            {{ todayBookingCount }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Weekly calendar -->
    <div class="d-flex align-center mb-3 mt-2">
      <div class="subtitle-1 font-weight-bold">
        ตารางการรับสินค้าประจำสัปดาห์
      </div>
      <v-spacer />
      <v-btn
        rounded
        small
        color="white"
        class="mr-2"
        @click="goToToday">
        วันนี้
      </v-btn>
      <v-btn
        icon
        small
        @click="shiftWeek(-1)">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <span class="body-2 mx-2">{{ weekRangeLabel }}</span>
      <v-btn
        icon
        small
        @click="shiftWeek(1)">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <v-card
      rounded="lg"
      elevation="1"
      class="pa-4 mb-6">
      <v-progress-linear
        v-if="loadingWeek"
        indeterminate
        color="secondary"
        class="mb-3" />

      <v-row
        v-else
        no-gutters
        class="fill-height">
        <v-col
          v-for="day in weekDays"
          :key="day.date"
          cols
          class="px-1 d-flex">
          <v-card
            rounded="lg"
            :class="[
              'pa-3 text-center flex-grow-1',
              selectedDate === day.date ? 'selected-day' : '',
              day.isHoliday ? 'holiday-day' : 'cursor-pointer'
            ]"
            :elevation="selectedDate === day.date ? 3 : 0"
            :outlined="selectedDate !== day.date"
            @click="selectDay(day)">
            <div
              :class="[
                'caption font-weight-medium',
                isToday(day.date) ? 'primary--text' : 'grey--text'
              ]">
              {{ thaiDayShort(day.dayOfWeek) }}
            </div>
            <div
              :class="[
                'text-h6 font-weight-bold my-1',
                isToday(day.date) ? 'primary--text' : ''
              ]">
              {{ dayNumber(day.date) }}
            </div>
            <template>
              <div
                v-if="day.isHoliday"
                class="caption grey--text">
                หยุดทำการ
              </div>
              <div class="caption primary--text font-weight-medium">
                {{ day.totalBookings }} การจอง
              </div>
              <div
                v-if="day.earliestSlotTime"
                class="caption grey--text mt-1"
                style="font-size:10px!important">
                เร็วสุด: {{ day.earliestSlotTime }}
              </div>
              <div
                v-if="day.latestSlotTime"
                class="caption grey--text"
                style="font-size:10px!important">
                ช้าสุด: {{ day.latestSlotTime }}
              </div>
            </template>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

    <!-- Booking list for selected day -->
    <template v-if="selectedDay">
      <div class="d-flex align-center mb-3">
        <div class="subtitle-1 font-weight-bold">
          รายการจองสินค้าประจำสัปดาห์
        </div>
        <v-spacer />
        <v-text-field
          v-model="search"
          dense
          outlined
          rounded
          hide-details
          placeholder="ค้นหาชื่อโรงงาน..."
          prepend-inner-icon="mdi-magnify"
          class="mr-3"
          style="max-width:240px" />
        <v-btn
          :disabled="!isHasPendingBooking"
          depressed
          color="secondary"
          class="white--text"
          @click="printDailyGRN">
          พิมพ์ใบนัดหมายทั้งหมดของวันนี้
        </v-btn>
      </div>

      <v-card
        rounded="lg"
        elevation="1">
        <v-simple-table class="booking-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th
                  class="caption grey--text"
                  style="min-width:150px">
                  รหัสการจอง
                </th>
                <th class="caption grey--text">
                  ชื่อโรงงาน
                </th>
                <th class="caption grey--text">
                  เวลาเริ่ม-สิ้นสุด
                </th>
                <th class="caption grey--text">
                  Collections
                </th>
                <th class="caption grey--text">
                  สถานะ
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr v-if="baseFilteredBookings.length === 0">
                <td
                  colspan="6"
                  class="text-center grey--text py-6">
                  ไม่มีรายการจอง
                </td>
              </tr>
              <tr
                v-for="booking in baseFilteredBookings"
                :key="booking.id"
                class="cursor-pointer"
                @click="$router.push({ name: 'DeliveryBookingDetail', params: { id: booking.id } })">
                <td>
                  <span class="primary--text font-weight-medium body-2">
                    #{{ booking.bookingNo }}
                  </span>
                </td>
                <td>
                  <div
                    v-for="vendor in booking.vendors"
                    :key="vendor.vendorId"
                    class="d-flex align-center py-1">
                    <v-avatar
                      size="28"
                      color="grey lighten-3"
                      class="mr-2">
                      <v-icon
                        small
                        color="grey darken-1">
                        mdi-factory
                      </v-icon>
                    </v-avatar>
                    <span class="body-2 font-weight-medium">{{ vendor.vendorName }}</span>
                  </div>
                  <span
                    v-if="!booking.vendors || booking.vendors.length === 0"
                    class="caption grey--text">-</span>
                </td>
                <td>
                  <span
                    :class="[
                      'body-2 font-weight-medium',
                      isLateSlot(booking) ? 'error--text' : ''
                    ]">
                    {{ slotTime(booking) }}
                  </span>
                </td>
                <td>
                  <template v-if="booking.collections && booking.collections.length">
                    <v-chip
                      v-for="col in booking.collections"
                      :key="col"
                      small
                      outlined
                      class="mr-1 my-1">
                      {{ col }}
                    </v-chip>
                  </template>
                  <span
                    v-else
                    class="caption grey--text">-</span>
                </td>
                <td>
                  <v-chip
                    small
                    :color="statusColor(booking.status)"
                    :text-color="statusTextColor(booking.status)"
                    label>
                    {{ statusLabel(booking.status) }}
                  </v-chip>
                </td>
                <td class="text-right">
                  <v-menu
                    v-if="booking.status === 'pending' || booking.status === 'noshow'"
                    left
                    offset-y>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        small
                        v-on="on">
                        <v-icon small>
                          mdi-dots-vertical
                        </v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item @click="printGRN(booking)">
                        <v-list-item-icon class="mr-2">
                          <v-icon small>
                            mdi-printer
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="body-2">
                          พิมพ์ใบนัดหมาย
                        </v-list-item-title>
                      </v-list-item>
                      <v-divider v-if="booking.status === 'pending' && canReject" />
                      <v-list-item
                        v-if="booking.status === 'pending' && canReject"
                        @click.stop="openRejectDialog(booking)">
                        <v-list-item-icon class="mr-2">
                          <v-icon
                            small
                            color="error">
                            mdi-cancel
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="body-2 error--text">
                          ปฏิเสธการจอง
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card>
    </template>

    <!-- Reject Dialog -->
    <v-dialog
      v-model="confirmReject"
      max-width="460">
      <v-card rounded="lg">
        <v-card-title class="subtitle-1 font-weight-bold">
          ยืนยันการปฏิเสธการจอง
        </v-card-title>
        <v-card-text>
          <div class="body-2 mb-4">
            คุณต้องการปฏิเสธการจอง
            <strong>#{{ rejectTarget && rejectTarget.bookingNo }}</strong>
            <template v-if="rejectTarget && rejectTarget.date">
              วันที่ <strong>{{ $dayjs(rejectTarget.date).locale('th').format('DD MMMM YYYY') }}</strong>
            </template>
            ใช่หรือไม่?
          </div>
          <div class="caption grey--text mb-1">
            เหตุผลในการปฏิเสธ <span class="error--text">*</span>
          </div>
          <v-textarea
            v-model="rejectReason"
            outlined
            dense
            rows="3"
            :error-messages="rejectReason.trim() === '' && rejecting ? 'กรุณาระบุเหตุผล' : ''"
            placeholder="ระบุเหตุผล..." />
          <div
            class="caption"
            style="color:#f97316">
            <v-icon
              x-small
              style="color:#f97316">
              mdi-information-outline
            </v-icon>
            เหตุผลนี้จะแสดงให้ Vendor เห็น
          </div>
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-spacer />
          <v-btn
            text
            @click="confirmReject = false">
            ยกเลิก
          </v-btn>
          <v-btn
            depressed
            color="error"
            :loading="rejecting"
            :disabled="!rejectReason.trim()"
            @click="rejectBooking">
            ยืนยันปฏิเสธ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import DeliveryBookingProvider from '@/resources/DeliveryBookingProvider'
import { mapActions } from 'vuex'
import { getRole } from '@/assets/js/Authentication'
import DeliveryBookingListFilter from './components/DeliveryBookingListFilter.vue'
import {
  statusLabel,
  statusBg,
  statusTextColor
} from '@/assets/js/BookingStatusUtils'
import { sortByTimeFn } from '@/assets/js/DateTimeUtils'

const DAY_NAMES_FULL = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']

export default {
  name: 'DeliveryBookingList',

  components: {
    DeliveryBookingListFilter
  },

  data () {
    return {
      deliveryBookingService: new DeliveryBookingProvider(),

      weekStart: null,
      weekData: null,
      loadingWeek: false,

      selectedDate: null,
      search: '',
      todayBookingCount: 0,
      filterDrawer: false,
      filters: {
        search: '',
        vendors: [],
        collections: [],
        itemNo: ''
      },

      confirmReject: false,
      rejecting: false,
      rejectTarget: null,
      rejectReason: ''
    }
  },

  computed: {
    canReject () {
      const role = getRole()
      return !['merchandiser', 'merchandising_planner'].includes(role)
    },

    today () {
      return this.$dayjs().format('DD-MM-YYYY')
    },

    todayLabel () {
      return this.$dayjs().locale('th').format('DD-MM-YYYY')
    },

    weekRangeLabel () {
      const end = this.weekStart.add(6, 'day')
      const fmt = (d) => d.locale('th').format('DD-MM-YYYY')
      return `${fmt(this.weekStart)} - ${fmt(end)}`
    },

    weekDays () {
      return this.weekData ? this.weekData.days : []
    },

    selectedDay () {
      return this.weekDays.find((d) => d.date === this.selectedDate) || null
    },

    allBookings () {
      return this.selectedDay ? this.selectedDay.bookings : []
    },

    activeFilterCount () {
      return this.filters.vendors.length
        + this.filters.collections.length
        + (this.filters.itemNo?.trim() ? 1 : 0)
    },

    baseFilteredBookings () {
      let list = this.allBookings

      const q = this.search.trim().toLowerCase()
      if (q) {
        list = list.filter((b) => this.vendorName(b).toLowerCase().includes(q)
          || b.bookingNo.toLowerCase().includes(q))
      }

      return list
    },

    isHasPendingBooking () {
      return this.allBookings?.some((bk) => bk.status === 'pending' || bk.status === 'noshow')
    }

  },

  created () {
    this.weekStart = this.$dayjs()
  },

  async mounted () {
    const today = this.$dayjs()
    this.selectedDate = null
    await this.fetchWeek()
    const todayDay = this.weekDays.find((d) => this.$dayjs(d.date).isSame(today, 'day'))
    if (todayDay) this.selectedDate = todayDay.date
  },

  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),

    async fetchWeek () {
      this.loadingWeek = true
      try {
        const startDate = this.weekStart.format('YYYY-MM-DD')
        const { data } = await this.deliveryBookingService.getWeeklyBookings(startDate)
        this.weekData = data
        const todayDay = data.days.find((d) => this.$dayjs(d.date).isSame(this.$dayjs(), 'day'))
        if (todayDay) {
          this.todayBookingCount = todayDay.totalBookings
        }
        if (!this.selectedDate || !data.days.find((d) => d.date === this.selectedDate)) {
          const firstNonHoliday = data.days.find((d) => !d.isHoliday)
          if (firstNonHoliday) this.selectedDate = firstNonHoliday.date
        }
      } catch (e) {
        this.setSnackbar({
          value: true,
          message: 'โหลดข้อมูลไม่สำเร็จ',
          type: 'error'
        })
      } finally {
        this.loadingWeek = false
      }
    },

    async shiftWeek (delta) {
      this.weekStart = this.weekStart.add(delta * 7, 'day')
      await this.fetchWeek()
    },

    selectDay (day) {
      this.selectedDate = day.date
      this.search = ''
    },

    async goToToday () {
      const today = this.$dayjs()
      this.weekStart = today
      this.selectedDate = null
      await this.fetchWeek()
      const todayDay = this.weekDays.find((d) => this.$dayjs(d.date).isSame(today, 'day'))
      if (todayDay) this.selectedDate = todayDay.date
    },

    printGRN (booking) {
      const { href } = this.$router.resolve({
        name: 'DeliveryBookingGRN',
        params: { id: booking.id }
      })
      window.open(href, '_blank')
    },

    openRejectDialog (booking) {
      this.rejectTarget = booking
      this.confirmReject = true
    },

    async rejectBooking () {
      this.rejecting = true
      try {
        await this.deliveryBookingService.rejectBooking(
          this.rejectTarget.id,
          this.rejectReason.trim() || undefined
        )
        this.setSnackbar({ value: true, message: 'ปฏิเสธการจองสำเร็จ', type: 'success' })
        this.confirmReject = false
        await this.fetchWeek()
      } catch (e) {
        const msg = e?.response?.data?.message || 'ปฏิเสธไม่สำเร็จ'
        this.setSnackbar({ value: true, message: msg, type: 'error' })
      } finally {
        this.rejecting = false
        this.rejectTarget = null
        this.rejectReason = ''
      }
    },

    printDailyGRN () {
      const { href } = this.$router.resolve({
        name: 'DeliveryBookingDailyGRN',
        query: { date: this.selectedDate }
      })
      window.open(href, '_blank')
    },

    clearFilters () {
      this.filters = { search: '', vendors: [], collections: [], itemNo: '' }
    },

    isToday (date) {
      return date === this.today
    },

    thaiDayShort (dow) {
      return DAY_NAMES_FULL[dow] || ''
    },

    dayNumber (date) {
      return this.$dayjs(date).date()
    },

    vendorName (booking) {
      if (!booking.vendors || booking.vendors.length === 0) return '-'
      return booking.vendors.map((v) => v.vendorName).join(', ')
    },

    slotTime (booking) {
      if (!booking.slots || booking.slots.length === 0) return '-'
      const sorted = sortByTimeFn(booking.slots, (s) => s.startTime)
      return `${sorted[0].startTime} - ${sorted[sorted.length - 1].endTime}`
    },

    isLateSlot (booking) {
      if (!booking.slots || booking.slots.length === 0) return false
      const now = this.$dayjs()
      const [h, m] = booking.slots[0].startTime.split(':').map(Number)
      const slotStart = this.$dayjs().hour(h).minute(m)
      return booking.date === this.today && now.isAfter(slotStart)
    },

    statusLabel: (s) => statusLabel(s),
    statusColor: (s) => statusBg(s),
    statusTextColor: (s) => statusTextColor(s)
  }
}
</script>

<style scoped>
.selected-day {
  border: 2px solid #155e74 !important;
  background-color: #f0f9ff;
}

.holiday-day {
  background-color: #f5f5f5;
  opacity: 0.7;
}

.cursor-pointer {
  cursor: pointer;
}

::v-deep .v-card:focus::before {
  opacity: 0 !important;
}

::v-deep .booking-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding-top: 16px !important;
  padding-bottom: 16px !important;
}

::v-deep .booking-table > .v-data-table__wrapper > table > thead > tr > th {
  padding-top: 14px !important;
  padding-bottom: 14px !important;
}
</style>
